import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { useAdminPanelStore } from "features/AdminPanel/store/useAdminPanelStore";
import { PAYMENT_OPTION_KEYS } from "features/Account/modules/PaymentOptions/constants/PaymentOptions.constants";
import { ADMIN_PANEL_SECTION_CONFIG_FE_STATUS } from "features/AdminPanel/constants/AdminPanel.constants";
import { useAccountSectionUnlocking } from "features/Account/utils/useAccountSectionChange";
import { UPDATE_ADMIN_PANEL_SETTINGS_LISITNG_LEVEL } from "features/AdminPanel/constants/AdminPanel.api";

import { useInternationalPaymentStore } from "../stores/useInternationalPaymentStore";
import {
  getInitialMetaState,
  isFormValid,
} from "./useInternationalPaymentForm";
import {
  INTERNATIONAL_PAYMENT_FIELDS_CONFIG,
  INTERNATIONAL_PAYMENT_TEXTS,
  DEBOUNCE_DELAY,
} from "../../modules/InternationalPayment/InternationalPayment.constants";
import useDebounce from "hooks/useDebounce";

export const useInternationalPaymentAutoSave = () => {
  const INTERNATIONAL_PAYMENT_KEY = PAYMENT_OPTION_KEYS.INTERNATIONAL_PAYMENT;

  const { listing_uuid } = useParams();
  const { handleSectionChange } = useAccountSectionUnlocking();

  const sectionConfig = useAdminPanelStore((state) => state.sectionConfig);
  const setSectionConfig = useAdminPanelStore(
    (state) => state.setSectionConfig
  );

  const { isEnabled, addressData, setAddressData } =
    useInternationalPaymentStore();

  const [formValues, setFormValues] = useState(() => addressData ?? {});
  const [formMeta, setFormMeta] = useState(getInitialMetaState);

  const isFirstRender = useRef(true);

  const { setValue: setDebouncedValues, debouncedValue } = useDebounce({
    inititalValue: addressData ?? {},
    delay: DEBOUNCE_DELAY,
  });

  useEffect(() => {
    if (addressData) {
      setFormValues(addressData);
      setDebouncedValues(addressData);
    }
  }, [addressData]);

  const setStatus = (feStatus, extraValue = {}) => {
    setSectionConfig({
      [INTERNATIONAL_PAYMENT_KEY]: {
        ...sectionConfig[INTERNATIONAL_PAYMENT_KEY],
        ...extraValue,
        FE_METADATA: { UPDATION_STATUS: { STATUS: feStatus } },
      },
    });
  };

  const handleAutoSave = async (values) => {
    const apiOptions = {
      ...(listing_uuid && {
        endPoint: UPDATE_ADMIN_PANEL_SETTINGS_LISITNG_LEVEL,
      }),
      payload: {
        feature_key: INTERNATIONAL_PAYMENT_KEY,
        feature_value: { is_enabled: isEnabled, ...values },
        ...(listing_uuid && { listing_uuid }),
      },
    };

    setStatus(ADMIN_PANEL_SECTION_CONFIG_FE_STATUS.SAVING);

    try {
      const response = await handleSectionChange(apiOptions);

      if (response) {
        setStatus(ADMIN_PANEL_SECTION_CONFIG_FE_STATUS.SAVED, {
          value: { is_enabled: true, ...values },
        });
        setAddressData(values);
      }
    } catch (error) {
      setStatus(ADMIN_PANEL_SECTION_CONFIG_FE_STATUS.ERROR);
    }
  };

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    if (!debouncedValue || !isFormValid(debouncedValue)) return;

    handleAutoSave(debouncedValue);
  }, [debouncedValue]);

  const handleChange = (val, name) => {
    const updated = { ...formValues, [name]: val };
    setFormValues(updated);

    const isRequiredAndEmpty =
      INTERNATIONAL_PAYMENT_FIELDS_CONFIG[name]?.required && !val?.trim();

    setFormMeta((prev) => ({
      ...prev,
      [name]: {
        touched: true,
        error: isRequiredAndEmpty
          ? INTERNATIONAL_PAYMENT_TEXTS.REQUIRED_ERROR
          : "",
      },
    }));

    // Update the debounce hook
    if (!isRequiredAndEmpty) {
      setDebouncedValues(updated);
    }
  };

  return {
    formValues,
    formMeta,
    isEnabled,
    addressData,
    handleChange,
  };
};
